import React, { useRef, useState } from "react";
import { Trash } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { COLORS, STAMPS, SCHRIFTSATZ_KATEGORIEN, clamp01, isDrawingTool, bbox } from "./annotUtils";

export default function AnnotOverlay({ annots, pageSize, page, tool, color, stroke, stamp, isImage, selectedId, onSelect, onCreate, onUpdate, onDelete }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(null);
  const drawing = useRef(null);
  const { w, h } = pageSize;
  const active = isDrawingTool(tool, isImage);
  const selectable = tool === "select";

  const norm = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: clamp01((e.clientX - r.left) / r.width), y: clamp01((e.clientY - r.top) / r.height) };
  };

  const down = (e) => {
    if (!active || e.button > 0) return;
    e.preventDefault();
    ref.current.setPointerCapture?.(e.pointerId);
    const p = norm(e);
    if (tool === "pin") {
      onCreate({ typ: "pin", page, farbe: color, pos: p, notiz: "" }, { openEditor: true });
      return;
    }
    if (tool === "stamp") {
      const sw = Math.min(0.9, 170 / w), sh = Math.min(0.5, 46 / h);
      const s = STAMPS.find(x => x.key === stamp) || STAMPS[0];
      onCreate({ typ: "stamp", page, farbe: s.color, stempel: s.key, rect: { x: clamp01(p.x - sw / 2), y: clamp01(p.y - sh / 2), w: sw, h: sh } });
      return;
    }
    drawing.current = { start: p, points: [p] };
    setDraft(tool === "pen" ? { typ: "pen", path: [[p.x, p.y]] } : { typ: tool, rect: { x: p.x, y: p.y, w: 0, h: 0 } });
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = norm(e);
    if (tool === "pen") {
      const pts = drawing.current.points;
      const last = pts[pts.length - 1];
      if (Math.hypot(p.x - last.x, p.y - last.y) > 0.0015) {
        pts.push(p);
        setDraft({ typ: "pen", path: pts.map(q => [q.x, q.y]) });
      }
    } else {
      const s = drawing.current.start;
      setDraft({ typ: tool, rect: { x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) } });
    }
  };

  const up = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const d = draft;
    drawing.current = null;
    setDraft(null);
    if (!d) return;
    if (d.typ === "pen") {
      if (d.path.length >= 2) onCreate({ typ: "pen", page, farbe: color, stroke, path: d.path });
    } else if (d.rect.w > 0.004 && d.rect.h > 0.004) {
      if (d.typ === "highlight") onCreate({ typ: "highlight", page, farbe: color, rects: [d.rect], text: null });
      else onCreate({ typ: "rect", page, farbe: color, stroke, rect: d.rect });
    }
  };

  const pick = (e, a) => {
    if (!selectable) return;
    e.stopPropagation();
    onSelect(a.id === selectedId ? null : a.id);
  };

  const selected = annots.find(a => a.id === selectedId);
  const sb = selected ? bbox(selected) : null;
  const sw = (a) => Math.max(1.5, (a.stroke || 2) * (w / 800));

  return (
    <div
      ref={ref}
      className="absolute inset-0 z-10"
      style={{ pointerEvents: active ? "auto" : "none", touchAction: active ? "none" : "auto", cursor: active ? (tool === "pin" || tool === "stamp" ? "copy" : "crosshair") : "auto" }}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      data-testid="annot-overlay"
      data-tool={tool}
    >
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ overflow: "visible" }}>
        <g style={{ mixBlendMode: "multiply" }}>
          {annots.filter(a => a.typ === "highlight").map(a => (
            <g key={a.id} onClick={(e) => pick(e, a)} style={{ pointerEvents: selectable ? "auto" : "none", cursor: "pointer" }} data-testid={`annot-${a.id}`}>
              {(a.rects?.length ? a.rects : a.rect ? [a.rect] : []).map((r, i) => (
                <rect key={i} x={r.x * w} y={r.y * h} width={r.w * w} height={r.h * h} fill={a.farbe} fillOpacity={0.45} rx={1.5} />
              ))}
            </g>
          ))}
        </g>
        {annots.filter(a => a.typ === "rect").map(a => (
          <rect key={a.id} x={a.rect.x * w} y={a.rect.y * h} width={a.rect.w * w} height={a.rect.h * h} fill={a.farbe} fillOpacity={0.06} stroke={a.farbe} strokeWidth={sw(a)} rx={2}
            onClick={(e) => pick(e, a)} style={{ pointerEvents: selectable ? "auto" : "none", cursor: "pointer" }} data-testid={`annot-${a.id}`} />
        ))}
        {annots.filter(a => a.typ === "pen" && a.path?.length > 1).map(a => (
          <g key={a.id} onClick={(e) => pick(e, a)} style={{ pointerEvents: selectable ? "auto" : "none", cursor: "pointer" }} data-testid={`annot-${a.id}`}>
            <polyline points={a.path.map(p => `${p[0] * w},${p[1] * h}`).join(" ")} fill="none" stroke="transparent" strokeWidth={Math.max(12, sw(a) * 4)} />
            <polyline points={a.path.map(p => `${p[0] * w},${p[1] * h}`).join(" ")} fill="none" stroke={a.farbe} strokeWidth={sw(a)} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
        {draft?.typ === "pen" && <polyline points={draft.path.map(p => `${p[0] * w},${p[1] * h}`).join(" ")} fill="none" stroke={color} strokeWidth={Math.max(1.5, stroke * (w / 800))} strokeLinecap="round" strokeLinejoin="round" />}
        {draft?.rect && (
          <rect x={draft.rect.x * w} y={draft.rect.y * h} width={draft.rect.w * w} height={draft.rect.h * h}
            fill={color} fillOpacity={draft.typ === "highlight" ? 0.4 : 0.08} stroke={draft.typ === "highlight" ? "none" : color} strokeWidth={Math.max(1.5, stroke * (w / 800))} strokeDasharray="6 4" />
        )}
        {sb && <rect x={sb.x * w - 4} y={sb.y * h - 4} width={sb.w * w + 8} height={sb.h * h + 8} fill="none" stroke="#1A2238" strokeWidth={1.5} strokeDasharray="5 4" rx={3} pointerEvents="none" />}
      </svg>

      {annots.filter(a => a.typ === "stamp").map(a => {
        const s = STAMPS.find(x => x.key === a.stempel);
        const fs = Math.max(9, Math.min(a.rect.h * h * 0.45, (a.rect.w * w) / Math.max(1, (s?.label || "").length) * 1.5, 26));
        return (
          <div key={a.id} onClick={(e) => pick(e, a)} data-testid={`annot-${a.id}`}
            className="absolute flex items-center justify-center font-bold uppercase tracking-wider select-none"
            style={{ left: a.rect.x * w, top: a.rect.y * h, width: a.rect.w * w, height: a.rect.h * h, border: `${Math.max(1.5, 2.2 * (w / 800))}px solid ${a.farbe}`, color: a.farbe, fontSize: fs, borderRadius: 3, transform: "rotate(-2deg)", pointerEvents: selectable ? "auto" : "none", cursor: "pointer", fontFamily: "Helvetica, Arial, sans-serif" }}>
            {s?.label || (a.stempel || "").toUpperCase()}
          </div>
        );
      })}

      {annots.filter(a => a.typ === "pin").map((a, i) => (
        <button key={a.id} type="button" onClick={(e) => pick(e, a)} data-testid={`annot-${a.id}`} title={a.notiz || "Kommentar"}
          className={`absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center shadow-md border-2 border-white transition-transform ${selectable ? "hover:scale-110" : ""}`}
          style={{ left: a.pos.x * w, top: a.pos.y * h, background: a.farbe, pointerEvents: selectable ? "auto" : "none" }}>
          {i + 1}
        </button>
      ))}

      {selected && sb && (
        <AnnotPopover a={selected} bb={sb} w={w} h={h} onUpdate={onUpdate} onDelete={onDelete} onClose={() => onSelect(null)} />
      )}
    </div>
  );
}

function AnnotPopover({ a, bb, w, h, onUpdate, onDelete, onClose }) {
  const [notiz, setNotiz] = useState(a.notiz || "");
  const width = 280;
  const left = Math.max(4, Math.min(w - width - 4, bb.x * w));
  const below = (bb.y + bb.h) * h + 10;
  const top = below + 200 > h ? Math.max(4, bb.y * h - 210) : below;
  const commitNotiz = () => { if ((a.notiz || "") !== notiz) onUpdate(a.id, { notiz }); };

  return (
    <div className="absolute z-20 bg-white rounded-lg shadow-[0_12px_32px_rgba(10,25,47,0.25)] border border-[#E5E7EB] p-3 space-y-2.5 text-xs" style={{ left, top, width, pointerEvents: "auto" }}
      onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} data-testid="annot-popover">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {COLORS.map(c => (
            <button key={c.hex} type="button" title={c.label} onClick={() => onUpdate(a.id, { farbe: c.hex })}
              className={`h-5 w-5 rounded-full border-2 transition-transform hover:scale-110 ${a.farbe === c.hex ? "border-[#0A192F] scale-110" : "border-white shadow"}`} style={{ background: c.hex }} data-testid={`annot-color-${c.hex.slice(1)}`} />
          ))}
        </div>
        <button type="button" onClick={() => onDelete(a.id)} className="h-7 w-7 rounded-md text-[#D32F2F] hover:bg-[#FEF2F2] flex items-center justify-center" title="Markierung löschen" data-testid="annot-delete"><Trash size={15} /></button>
      </div>
      {a.typ === "stamp" && (
        <Select value={a.stempel} onValueChange={(v) => onUpdate(a.id, { stempel: v, farbe: STAMPS.find(s => s.key === v)?.color || a.farbe })}>
          <SelectTrigger className="h-8 text-xs" data-testid="annot-stamp-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STAMPS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {a.typ === "highlight" && a.text && (
        <>
          <p className="text-[11px] text-[#4B5563] leading-snug line-clamp-3 bg-[#FAFAFA] rounded p-1.5">„{a.text}“</p>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">In Schriftsatz übernehmen als</p>
            <Select value={a.schriftsatz_kategorie || "none"} onValueChange={(v) => onUpdate(a.id, { schriftsatz_kategorie: v === "none" ? null : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="annot-schriftsatz-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— nicht übernehmen —</SelectItem>
                {SCHRIFTSATZ_KATEGORIEN.map(k => <SelectItem key={k.value} value={k.value}><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: k.color }} />{k.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      <Textarea autoFocus={a.typ === "pin" && !a.notiz} rows={a.typ === "pin" ? 3 : 2} value={notiz} onChange={(e) => setNotiz(e.target.value)} onBlur={commitNotiz}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { commitNotiz(); onClose(); } }}
        placeholder={a.typ === "pin" ? "Kommentar eingeben …" : "Notiz (optional)"} className="text-xs min-h-0" data-testid="annot-notiz" />
      <div className="flex justify-end"><button type="button" onClick={() => { commitNotiz(); onClose(); }} className="text-[11px] font-medium text-[#1A2238] hover:underline" data-testid="annot-popover-close">Fertig</button></div>
    </div>
  );
}
